import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';
import {
  BaseApiService,
  CompetencesTemplateDictionaryItem,
  DictionariesListModel,
  DictionaryItemModel,
  EducationDictionaryItem,
  GroupedAutocompleteOptions,
  NicheEnum,
} from '@eagle/shared';
import {
  CompetenceTemplate,
  ListVacancyPayload,
  PublishedChannelsEnum,
  VacancyModel,
} from '../models';
import { ConfigurationAPISetting } from '../../../api';
import { environment } from '@eagle/env';

@Injectable({
  providedIn: 'root',
})
export class VacanciesApiService {
  private baseApiService = inject(BaseApiService);

  private readonly endpoint = `${ConfigurationAPISetting.endpoint}/vacancies`;

  getSuggestions(term: string): Observable<GroupedAutocompleteOptions[]> {
    return this.baseApiService.get(`${this.endpoint}/suggestions`, { term });
  }

  getAll(params: Params = {}): Observable<ListVacancyPayload> {
    return this.baseApiService.get(this.endpoint, { ...params });
  }

  get(id: string): Observable<VacancyModel> {
    return this.baseApiService.get(`${this.endpoint}/${id}`);
  }

  create(entity: Partial<VacancyModel> | null): Observable<VacancyModel> {
    return this.baseApiService.post(this.endpoint, entity);
  }

  update(entityId: string, entity: VacancyModel): Observable<VacancyModel> {
    return this.baseApiService.put(`${this.endpoint}/${entityId}`, entity);
  }

  delete(entityIds: string[]): Observable<VacancyModel> {
    return entityIds.length === 1
      ? this.baseApiService.delete(`${this.endpoint}/${entityIds[0]}`)
      : this.baseApiService.post(`${this.endpoint}/delete`, entityIds);
  }

  publish(
    id: string,
    payload: {
      id: string;
      channels: PublishedChannelsEnum[];
    }
  ) {
    return this.baseApiService.post(`${this.endpoint}/${id}/publish`, payload);
  }

  closeVacancy(id: string): Observable<VacancyModel> {
    return this.baseApiService.post(`${this.endpoint}/${id}/close`);
  }

  createVacancyByAi(
    officeId: string,
    companyId: string,
    rawDescription: string
  ): Observable<VacancyModel> {
    return this.baseApiService.post(`${this.endpoint}/ai`, {
      officeId,
      companyId,
      rawDescription,
    });
  }

  updateVacancyByAI(
    id: string,
    payload: {
      code: string;
      niche: string;
    }
  ): Observable<VacancyModel> {
    return this.baseApiService.post(`${this.endpoint}/${id}/ai/job-description`, payload);
  }

  getCompetences(searchTerm: string) {
    return this.baseApiService.get<{
      jobTemplates: Array<CompetencesTemplateDictionaryItem>;
    }>(`${environment.vacanciesApiUrl}/competences`, {
      searchTerm,
    });
  }

  getCompetenceTemplate(templateId: string): Observable<CompetenceTemplate> {
    return this.baseApiService.get(
      `${environment.vacanciesApiUrl}/competences/templates/${templateId}`
    );
  }

  getVacanciesDictionaries(types: string[] = []): Observable<DictionariesListModel> {
    const typesList = types.join(',');
    return this.baseApiService.get(`${this.endpoint}/dictionaries`, {
      types: typesList,
    });
  }

  getAIVoicesDictionary(niche: NicheEnum): Observable<DictionaryItemModel[]> {
    return this.baseApiService.get<DictionaryItemModel[]>(
      `${environment.vacanciesApiUrl}/dictionaries/ai/ai-voices`,
      { niche }
    );
  }

  getVacancyEducationsDictionary(): Observable<EducationDictionaryItem[]> {
    return this.baseApiService.get<EducationDictionaryItem[]>(
      `${environment.vacanciesApiUrl}/dictionaries/educations`
    );
  }
}
